import { z } from 'zod'
import { normalizedHexColorSchema } from '../validators/tenant.validator.js'
import { mcpPrincipalKindSchema } from '../enums/mcp-principal-kind.enum.js'
import { tenantModeSchema } from '../enums/tenant-mode.enum.js'
import { uuidSchema } from '../validators/params.js'
import { cornerRadiusSchema } from '../enums/corner-radius.enum.js'
import { fontFamilySchema } from '../enums/font-family.enum.js'

/** An API key as the dashboard lists it. The secret is never part of this. */
export const apiKeyDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  mode: tenantModeSchema,
  /** The start of the secret, enough to recognize it (`psk_live_AbC1234`). */
  prefix: z.string(),
  /** Null for keys made before the dashboard (seeded or migrated). */
  createdBy: z
    .object({ kind: mcpPrincipalKindSchema, name: z.string() })
    .nullable(),
  createdAt: z.string().datetime(),
  lastUsedAt: z.string().datetime().nullable(),
  revokedAt: z.string().datetime().nullable(),
})

export type ApiKeyDto = z.infer<typeof apiKeyDtoSchema>

export const apiKeyListDtoSchema = z.object({
  keys: z.array(apiKeyDtoSchema),
})

export type ApiKeyListDto = z.infer<typeof apiKeyListDtoSchema>

/** The only response that ever carries a key's secret. */
export const createdApiKeyDtoSchema = z.object({
  key: apiKeyDtoSchema,
  secret: z.string(),
})

export type CreatedApiKeyDto = z.infer<typeof createdApiKeyDtoSchema>

/** One mode's integration settings, without the webhook secret itself. */
export const modeSettingsDtoSchema = z.object({
  mode: tenantModeSchema,
  webhookUrl: z.string().nullable(),
  embedOrigins: z.array(z.string()),
  hasWebhookSecret: z.boolean(),
  /** When the current webhook URL last accepted a delivery or test event (2xx); null until it has. */
  webhookVerifiedAt: z.string().datetime().nullable(),
  /** While a rotation overlap runs, when the previous secret stops signing deliveries; null otherwise. */
  previousSecretExpiresAt: z.string().datetime().nullable(),
})

export type ModeSettingsDto = z.infer<typeof modeSettingsDtoSchema>

export const tenantSettingsDtoSchema = z.object({
  modes: z.array(modeSettingsDtoSchema),
})

export type TenantSettingsDto = z.infer<typeof tenantSettingsDtoSchema>

export const webhookSecretDtoSchema = z.object({
  secret: z.string(),
})

export type WebhookSecretDto = z.infer<typeof webhookSecretDtoSchema>

/** The organization's profile as its dashboard shows and edits it. */
export const tenantProfileDtoSchema = z.object({
  slug: z.string(),
  name: z.string(),
  displayName: z.string(),
  fromName: z.string(),
  replyTo: z.string(),
  logoUrl: z.string().nullable(),
  /** Our stored copy of the logo, as a path on the API (load it from there; never from logoUrl). Null when there's none. */
  logoSrc: z.string().nullable(),
  accentColor: normalizedHexColorSchema.nullable(),
  cornerRadius: cornerRadiusSchema,
  fontFamily: fontFamilySchema,
  timezone: z.string(),
  /** What the organization charges a signer for a paper copy; 0 is free. Signers see it in the consent. */
  paperCopyFeeMicros: z.number().int(),
})

export type TenantProfileDto = z.infer<typeof tenantProfileDtoSchema>

/** The steps toward going live, in order. Each is checked from the tenant's own data. */
export const GO_LIVE_ITEMS = [
  'test_key',
  'test_webhook_verified',
  'test_envelope_finished',
  'live_webhook_url',
  'embed_origins',
  'live_key',
] as const

export const goLiveItemIdSchema = z.enum(GO_LIVE_ITEMS)

export type GoLiveItemId = z.infer<typeof goLiveItemIdSchema>

/**
 * A non-blocking go-live checklist: nothing here gates live keys. `optional`
 * items apply only to some integrations (embed origins matter only if you
 * embed signing). `ready` is true once every required item is done.
 */
export const goLiveChecklistDtoSchema = z.object({
  items: z.array(z.object({ id: goLiveItemIdSchema, done: z.boolean(), optional: z.boolean() })),
  ready: z.boolean(),
})

export type GoLiveChecklistDto = z.infer<typeof goLiveChecklistDtoSchema>
